import React, { useMemo } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  GiftIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import Button from '../ui/Button';

interface Tier {
  id: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  billingCycle: 'MONTHLY' | 'YEARLY';
  benefits: string[];
  isPublic: boolean;
  currentPatrons: number;
  maxPatrons?: number;
  customButtonText?: string;
  accessUrl?: string;
  sortOrder: number;
  isActive: boolean;
  freeTrialDays?: number;
}

interface TierComparisonTableProps {
  tiers: Tier[];
  currentTierId?: string;
  onSelectTier: (tier: Tier, action: 'upgrade' | 'downgrade' | 'current' | 'new') => void;
  className?: string;
  showPatronCounts?: boolean;
  highlightRecommended?: boolean;
}

/**
 * Enhanced Tier Comparison Table for Patrons
 * Shows side-by-side comparison with current tier highlighted
 * and upgrade/downgrade actions
 */
const TierComparisonTable: React.FC<TierComparisonTableProps> = ({
  tiers,
  currentTierId,
  onSelectTier,
  className = '',
  showPatronCounts = true,
  highlightRecommended = true,
}) => {
  // Sort tiers by price (lowest to highest)
  const sortedTiers = useMemo(() => {
    return [...tiers].sort((a, b) => {
      // Normalize to monthly for comparison
      const aMonthly = a.billingCycle === 'YEARLY' ? a.amount / 12 : a.amount;
      const bMonthly = b.billingCycle === 'YEARLY' ? b.amount / 12 : b.amount;
      return aMonthly - bMonthly;
    });
  }, [tiers]);

  // Extract all unique benefits across all tiers
  const allBenefits = useMemo(() => {
    const benefitsSet = new Set<string>();
    tiers.forEach((tier) => {
      tier.benefits.forEach((benefit) => benefitsSet.add(benefit));
    });
    return Array.from(benefitsSet);
  }, [tiers]);

  // Find current tier
  const currentTier = currentTierId ? tiers.find(t => t.id === currentTierId) : null;
  const currentTierMonthlyAmount = currentTier 
    ? (currentTier.billingCycle === 'YEARLY' ? currentTier.amount / 12 : currentTier.amount)
    : 0;

  // Determine action for each tier
  const getTierAction = (tier: Tier): 'upgrade' | 'downgrade' | 'current' | 'new' => {
    if (!currentTierId) return 'new';
    if (tier.id === currentTierId) return 'current';
    
    const tierMonthly = tier.billingCycle === 'YEARLY' ? tier.amount / 12 : tier.amount;
    return tierMonthly > currentTierMonthlyAmount ? 'upgrade' : 'downgrade';
  };

  // Find recommended tier (one above current)
  const recommendedTierId = useMemo(() => {
    if (!currentTierId || !highlightRecommended) return null;
    const currentIndex = sortedTiers.findIndex(t => t.id === currentTierId);
    if (currentIndex === -1 || currentIndex === sortedTiers.length - 1) return null;
    return sortedTiers[currentIndex + 1].id;
  }, [sortedTiers, currentTierId, highlightRecommended]);

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount / 100);
  };

  const isTierFull = (tier: Tier): boolean => {
    return !!(tier.maxPatrons && tier.currentPatrons >= tier.maxPatrons);
  };

  const getButtonText = (tier: Tier): string => {
    const action = getTierAction(tier);
    if (action === 'current') return 'Current Tier';
    if (isTierFull(tier)) return 'Sold Out';
    if (tier.customButtonText) return tier.customButtonText;
    
    switch (action) {
      case 'upgrade':
        return 'Upgrade';
      case 'downgrade':
        return 'Downgrade';
      case 'new':
        return 'Join Tier';
      default:
        return 'Select';
    }
  };

  const getButtonVariant = (tier: Tier): 'primary' | 'secondary' | 'outline' => {
    const action = getTierAction(tier);
    if (action === 'current') return 'outline';
    if (action === 'upgrade') return 'primary';
    return 'secondary';
  };

  if (sortedTiers.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <CurrencyDollarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No tiers available</h3>
        <p className="text-gray-600">This creator hasn't set up any membership tiers yet.</p>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {/* Mobile View - Cards */}
      <div className="block lg:hidden space-y-6">
        {sortedTiers.map((tier) => {
          const action = getTierAction(tier);
          const isRecommended = tier.id === recommendedTierId;
          const isCurrent = tier.id === currentTierId;

          return (
            <div
              key={tier.id}
              className={`bg-white border-2 rounded-lg p-6 relative ${
                isCurrent 
                  ? 'border-green-500 shadow-lg' 
                  : isRecommended
                  ? 'border-orange-400 shadow-md'
                  : 'border-gray-200'
              }`}
            >
              {/* Badges */}
              <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
                {isCurrent && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                    <CheckCircleSolidIcon className="w-4 h-4" />
                    Current
                  </span>
                )}
                {isRecommended && !isCurrent && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                    <StarIcon className="w-4 h-4" />
                    Recommended
                  </span>
                )}
                {isTierFull(tier) && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                    Sold Out
                  </span>
                )}
              </div>

              <div className="mb-4 pr-20">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{tier.description}</p>
                
                <div className="flex items-baseline mb-2">
                  <span className="text-4xl font-bold text-gray-900">
                    {formatCurrency(tier.amount, tier.currency)}
                  </span>
                  <span className="ml-2 text-gray-600">
                    / {tier.billingCycle === 'MONTHLY' ? 'month' : 'year'}
                  </span>
                </div>

                {tier.freeTrialDays && tier.freeTrialDays > 0 && (
                  <div className="flex items-center gap-1 text-green-600 text-sm mb-2">
                    <GiftIcon className="w-4 h-4" />
                    <span>{tier.freeTrialDays}-day free trial</span>
                  </div>
                )}

                {showPatronCounts && (
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-1">
                      <UserGroupIcon className="w-4 h-4" />
                      <span>{tier.currentPatrons} patrons</span>
                    </div>
                    {tier.maxPatrons && (
                      <span>
                        {tier.maxPatrons - tier.currentPatrons} spots left
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-3 mb-6">
                <h4 className="font-semibold text-gray-900">Benefits:</h4>
                {tier.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>

              <Button
                className={`w-full ${
                  action === 'upgrade' ? 'bg-gradient-to-r from-orange-400 to-pink-500' : ''
                }`}
                variant={getButtonVariant(tier)}
                onClick={() => onSelectTier(tier, action)}
                disabled={isTierFull(tier) || action === 'current'}
              >
                <span className="flex items-center justify-center gap-2">
                  {action === 'upgrade' && <ArrowUpIcon className="w-4 h-4" />}
                  {action === 'downgrade' && <ArrowDownIcon className="w-4 h-4" />}
                  {getButtonText(tier)}
                </span>
              </Button>

              {action === 'upgrade' && !isCurrent && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Changes take effect at next billing cycle
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop View - Comparison Table */}
      <div className="hidden lg:block overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <div className="grid gap-4" style={{ gridTemplateColumns: `220px repeat(${sortedTiers.length}, 1fr)` }}>
            {/* Header Row */}
            <div className="h-12"></div>
            {sortedTiers.map((tier) => {
              const action = getTierAction(tier);
              const isRecommended = tier.id === recommendedTierId;
              const isCurrent = tier.id === currentTierId;

              return (
                <div
                  key={`header-${tier.id}`}
                  className={`bg-gradient-to-b from-gray-50 to-white border-2 rounded-t-lg p-6 relative ${
                    isCurrent 
                      ? 'border-green-500 shadow-lg' 
                      : isRecommended
                      ? 'border-orange-400 shadow-md'
                      : 'border-gray-200'
                  }`}
                >
                  {/* Badges */}
                  <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                    {isCurrent && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                        <CheckCircleSolidIcon className="w-3 h-3" />
                        Current
                      </span>
                    )}
                    {isRecommended && !isCurrent && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                        <StarIcon className="w-3 h-3" />
                        Recommended
                      </span>
                    )}
                    {isTierFull(tier) && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                        Sold Out
                      </span>
                    )}
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3 pr-16">{tier.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 min-h-[40px]">{tier.description}</p>
                  
                  <div className="mb-4">
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold text-gray-900">
                        {formatCurrency(tier.amount, tier.currency)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      per {tier.billingCycle === 'MONTHLY' ? 'month' : 'year'}
                    </p>
                  </div>

                  <Button
                    className={`w-full ${
                      action === 'upgrade' ? 'bg-gradient-to-r from-orange-400 to-pink-500' : ''
                    }`}
                    variant={getButtonVariant(tier)}
                    onClick={() => onSelectTier(tier, action)}
                    disabled={isTierFull(tier) || action === 'current'}
                    size="sm"
                  >
                    <span className="flex items-center justify-center gap-1">
                      {action === 'upgrade' && <ArrowUpIcon className="w-3 h-3" />}
                      {action === 'downgrade' && <ArrowDownIcon className="w-3 h-3" />}
                      {getButtonText(tier)}
                    </span>
                  </Button>

                  {action === 'upgrade' && !isCurrent && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      At next billing
                    </p>
                  )}
                </div>
              );
            })}

            {/* Free Trial Row */}
            <div className="bg-gray-50 p-4 flex items-center border-l-2 border-gray-200 font-semibold text-sm text-gray-700">
              <GiftIcon className="w-5 h-5 mr-2 text-gray-600" />
              Free Trial
            </div>
            {sortedTiers.map((tier) => (
              <div
                key={`trial-${tier.id}`}
                className={`bg-white border-x-2 p-4 flex items-center justify-center ${
                  tier.id === currentTierId ? 'border-green-500' : tier.id === recommendedTierId ? 'border-orange-400' : 'border-gray-200'
                }`}
              >
                {tier.freeTrialDays && tier.freeTrialDays > 0 ? (
                  <span className="text-green-600 font-semibold text-sm flex items-center gap-1">
                    <CheckCircleIcon className="w-5 h-5" />
                    {tier.freeTrialDays} days
                  </span>
                ) : (
                  <XCircleIcon className="w-5 h-5 text-gray-300" />
                )}
              </div>
            ))}

            {/* Patrons Count Row */}
            {showPatronCounts && (
              <>
                <div className="bg-gray-50 p-4 flex items-center border-l-2 border-gray-200 font-semibold text-sm text-gray-700">
                  <UserGroupIcon className="w-5 h-5 mr-2 text-gray-600" />
                  Current Patrons
                </div>
                {sortedTiers.map((tier) => (
                  <div
                    key={`patrons-${tier.id}`}
                    className={`bg-white border-x-2 p-4 flex flex-col items-center justify-center ${
                      tier.id === currentTierId ? 'border-green-500' : tier.id === recommendedTierId ? 'border-orange-400' : 'border-gray-200'
                    }`}
                  >
                    <span className="font-semibold text-gray-900">{tier.currentPatrons}</span>
                    {tier.maxPatrons && (
                      <span className="text-xs text-gray-500 mt-1">
                        of {tier.maxPatrons}
                      </span>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* Benefits Section Header */}
            <div className="bg-gray-50 p-4 border-l-2 border-gray-200 font-semibold text-sm text-gray-900 col-span-full">
              Benefits Comparison
            </div>

            {/* Benefits Rows */}
            {allBenefits.map((benefit, idx) => (
              <React.Fragment key={`benefit-${idx}`}>
                <div className="bg-gray-50 p-4 flex items-center border-l-2 border-gray-200 text-sm text-gray-700">
                  {benefit}
                </div>
                {sortedTiers.map((tier) => (
                  <div
                    key={`benefit-${tier.id}-${idx}`}
                    className={`bg-white border-x-2 p-4 flex items-center justify-center ${
                      tier.id === currentTierId ? 'border-green-500' : tier.id === recommendedTierId ? 'border-orange-400' : 'border-gray-200'
                    }`}
                  >
                    {tier.benefits.includes(benefit) ? (
                      <CheckCircleIcon className="w-6 h-6 text-green-500" />
                    ) : (
                      <XCircleIcon className="w-6 h-6 text-gray-300" />
                    )}
                  </div>
                ))}
              </React.Fragment>
            ))}

            {/* Bottom Border */}
            <div className="h-0"></div>
            {sortedTiers.map((tier) => (
              <div
                key={`bottom-${tier.id}`}
                className={`border-b-2 border-x-2 rounded-b-lg ${
                  tier.id === currentTierId ? 'border-green-500' : tier.id === recommendedTierId ? 'border-orange-400' : 'border-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <CheckCircleIcon className="w-5 h-5 text-green-500" />
          <span>Included</span>
        </div>
        <div className="flex items-center gap-2">
          <XCircleIcon className="w-5 h-5 text-gray-300" />
          <span>Not included</span>
        </div>
        {currentTierId && (
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-green-500 rounded"></div>
            <span>Your current tier</span>
          </div>
        )}
        {recommendedTierId && (
          <div className="flex items-center gap-2">
            <StarIcon className="w-5 h-5 text-orange-500" />
            <span>Recommended upgrade</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TierComparisonTable;
